import React from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from "@/components/Base/Dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Progress } from "@/components/Base/Progress";
import { cn } from "@/lib/utils";

interface StatusDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  currentTask: string;
  progress: number;
}

// Custom DialogContent without close button
const StatusDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-screen-sm translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
StatusDialogContent.displayName = "StatusDialogContent";

export const StatusDialog = ({
  open,
  onOpenChange,
  title,
  currentTask,
  progress,
}: StatusDialogProps) => {
  // Custom handler that only allows the dialog to be closed by the parent component
  const handleOpenChange = (newOpen: boolean) => {
    // Only call onOpenChange if it exists and the dialog is being closed
    if (onOpenChange && !newOpen) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <StatusDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="mb-2 text-sm text-muted-foreground">{currentTask}</p>
          <Progress value={progress} className="h-2" />
        </div>
      </StatusDialogContent>
    </Dialog>
  );
};
